
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RssFeedItem } from '../types';
import { LinkIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';

interface IndustryFeedCarouselProps {
  items: RssFeedItem[];
}

export const IndustryFeedCarousel: React.FC<IndustryFeedCarouselProps> = ({ items }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const visibleCount = 4; // Number of items visible at once

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 4000); // Rotate every 4 seconds

    return () => clearInterval(timer);
  }, [items.length, isPaused]);

  // Get the subset of items to display.
  // We use modulo arithmetic to create a circular list effect.
  const visibleItems = items.length > 0
    ? Array.from({ length: visibleCount }).map((_, i) => {
      const index = (currentIndex + i) % items.length;
      return items[index];
    }).filter(Boolean) // Filter out any potential undefineds
    : [];

  return (
    <div
      className="relative overflow-hidden h-[420px] w-full"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white dark:from-gray-800 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white dark:from-gray-800 to-transparent z-10 pointer-events-none" />

      <div className="flex flex-col gap-4 py-2">
        <AnimatePresence initial={false} mode="popLayout">
          {visibleItems.map((item, i) => {
            if (!item) return null;
            return (
              <motion.a
                key={`${item.id}-${currentIndex + i}`} // Unique key for animation continuity
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-lg bg-gray-50 hover:bg-gray-100 dark:bg-gray-700/50 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-600 shadow-sm group"
              >
                <p className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 line-clamp-2 mb-1">
                  {item.titleKey ? t(item.titleKey) : item.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between mt-2">
                  <span className="flex items-center gap-1 truncate max-w-[70%]">
                    <LinkIcon className="w-3 h-3 flex-shrink-0" /> {item.sourceKey ? t(item.sourceKey) : item.source}
                  </span>
                  <span className="flex-shrink-0">{item.pubDate}</span>
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2 opacity-80">
                  {item.snippetKey ? t(item.snippetKey) : item.snippet.replace(/<[^>]*>?/gm, '')}
                </p>
              </motion.a>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};
